import { FC } from 'react';
import EmptyState from '@/components/seop/EmptyState';
import type { SeopMonth, SeopScenario } from '@/types/seop';
import { fmtMarginPct, fmtMoneyBRL, fmtUnits } from '@/utils/seopFormat';
import { foraDoPlano, mesDoCenario, separarBackups } from '@/utils/seopCenario';

const CELL = 'py-2 pr-3 text-sm text-content-primary';
const BOTAO_LINHA = 'rounded-lg border border-theme-border px-2 py-1 text-xs';
const BOTAO_SECUNDARIO =
  'rounded-lg border border-theme-border px-4 py-2 text-sm text-content-secondary hover:text-content-primary disabled:opacity-50';

interface AcoesDaLista {
  months: SeopMonth[];
  selecionados: string[];
  /** Id do cenário aberto para leitura, para destacar a linha. */
  abertoId: string | null;
  onToggle: (id: string, marcado: boolean) => void;
  onAbrir: (s: SeopScenario) => void;
  onRestaurar: (s: SeopScenario) => void;
  /** Cenário salvo no mês errado: troca o mês a que ele pertence, sem mexer nos números. */
  onCorrigirMes: (s: SeopScenario) => void;
  onExcluir: (s: SeopScenario) => void;
}

type AcoesDaLinha = Pick<AcoesDaLista, 'onAbrir' | 'onRestaurar' | 'onCorrigirMes' | 'onExcluir'>;

/** Os botões de uma linha. Cenário de um mês que saiu do plano não tem onde ser aplicado. */
const BotoesDoCenario: FC<AcoesDaLinha & { s: SeopScenario; aberto: boolean }> = ({
  s,
  aberto,
  onAbrir,
  onRestaurar,
  onCorrigirMes,
  onExcluir,
}) => {
  const semMes = foraDoPlano(s);
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        data-testid={`seop-abrir-${s.id}`}
        onClick={() => onAbrir(s)}
        disabled={aberto}
        title="Mostra os números deste cenário, no mês dele, sem alterar o plano atual"
        className={`${BOTAO_LINHA} font-semibold text-content-primary hover:border-accent disabled:opacity-50`}
      >
        Abrir
      </button>
      <button
        type="button"
        data-testid={`seop-restaurar-${s.id}`}
        onClick={() => onRestaurar(s)}
        disabled={semMes}
        title={
          semMes
            ? 'O mês deste cenário não está no plano. Corrija o mês do cenário para poder aplicá-lo.'
            : 'Faz deste cenário o plano atual do mês dele (pede confirmação e guarda o plano de agora)'
        }
        className={`${BOTAO_LINHA} text-content-secondary hover:text-content-primary disabled:opacity-50`}
      >
        Tornar plano atual…
      </button>
      <button
        type="button"
        data-testid={`seop-cenario-mes-${s.id}`}
        onClick={() => onCorrigirMes(s)}
        title="Para cenário salvo no mês errado: muda o mês a que ele pertence, sem mexer nos números"
        className={`${BOTAO_LINHA} text-content-secondary hover:text-content-primary`}
      >
        Corrigir mês…
      </button>
      <button
        type="button"
        onClick={() => onExcluir(s)}
        className={`${BOTAO_LINHA} text-theme-danger hover:bg-theme-danger/10`}
      >
        Excluir
      </button>
    </div>
  );
};

/** Número do resumo do cenário; snapshot sem resumo (ou com cálculo quebrado) mostra traço. */
const doResumo = (valor: number | null | undefined, formatar: (v: number) => string): string =>
  typeof valor === 'number' ? formatar(valor) : '—';

const NUM = `${CELL} text-right tabular-nums text-content-secondary`;

const LinhaDoCenario: FC<AcoesDaLista & { s: SeopScenario }> = ({
  s,
  months,
  selecionados,
  abertoId,
  onToggle,
  ...acoes
}) => {
  const aberto = s.id === abertoId;
  return (
    <tr className={`border-b border-theme-border/50 ${aberto ? 'bg-accent/10' : ''}`} data-testid={`seop-cenario-${s.id}`}>
      <td className={CELL}>
        <input
          type="checkbox"
          aria-label={`Comparar ${s.name}`}
          checked={selecionados.includes(s.id)}
          onChange={(e) => onToggle(s.id, e.target.checked)}
        />
      </td>
      <td className={CELL}>
        {s.name}
        {aberto && <span className="ml-2 rounded-full border border-accent px-2 py-0.5 text-2xs text-accent">aberto</span>}
      </td>
      <td className={`${CELL} text-content-secondary`} title={s.month?.range}>
        {mesDoCenario(s, months)}
        {foraDoPlano(s) && <span className="ml-1 text-2xs text-content-muted">(fora do plano)</span>}
      </td>
      <td className={NUM}>{doResumo(s.summary?.net_qty, fmtUnits)}</td>
      <td className={NUM}>{doResumo(s.summary?.final_value, fmtMoneyBRL)}</td>
      <td className={NUM}>{doResumo(s.summary?.margin_rate, fmtMarginPct)}</td>
      <td className={`${CELL} text-content-secondary`}>{s.created_by_name || '—'}</td>
      <td className={`${CELL} text-content-secondary`}>{new Date(s.created_at).toLocaleString('pt-BR')}</td>
      <td className={CELL}>
        <BotoesDoCenario s={s} aberto={aberto} {...acoes} />
      </td>
    </tr>
  );
};

const Tabela: FC<AcoesDaLista & { scenarios: SeopScenario[] }> = ({ scenarios, ...acoes }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead>
        <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
          <th className="pb-2 pr-3 text-left">Comparar</th>
          <th className="pb-2 pr-3 text-left">Nome</th>
          <th className="pb-2 pr-3 text-left" title="O mês do plano a que o cenário pertence: é nele que o cenário abre e entra ao virar o plano atual">
            Mês
          </th>
          <th className="pb-2 pr-3 text-right" title="Números do cenário no mês em que foi salvo">
            Quantidade
          </th>
          <th className="pb-2 pr-3 text-right">R$ PMV geral</th>
          <th className="pb-2 pr-3 text-right">% Margem</th>
          <th className="pb-2 pr-3 text-left">Autor</th>
          <th className="pb-2 pr-3 text-left">Criado em</th>
          <th className="pb-2 pr-3 text-left">Ações</th>
        </tr>
      </thead>
      <tbody>
        {scenarios.map((s) => (
          <LinhaDoCenario key={s.id} s={s} {...acoes} />
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * A lista de cenários salvos. Os backups automáticos (criados pelo sistema antes de cada
 * restauração) ficam num grupo à parte e recolhido: são rede de segurança, não o histórico
 * que o planejador montou, e misturados escondiam os cenários de verdade.
 */
export const TabelaCenarios: FC<AcoesDaLista & { scenarios: SeopScenario[] }> = ({ scenarios, ...acoes }) => {
  const { salvos, backups } = separarBackups(scenarios);
  return (
    <>
      {salvos.length === 0 ? (
        <EmptyState
          icon="bookmark"
          title="Nenhum cenário salvo"
          hint="Salve o plano atual para poder comparar alternativas e voltar atrás depois."
        />
      ) : (
        <Tabela scenarios={salvos} {...acoes} />
      )}
      {backups.length > 0 && (
        <details className="mt-4 text-sm" data-testid="seop-backups">
          <summary className="cursor-pointer text-content-secondary">
            Backups automáticos ({backups.length}) — o plano como estava antes de cada restauração
          </summary>
          <div className="mt-2">
            <Tabela scenarios={backups} {...acoes} />
          </div>
        </details>
      )}
    </>
  );
};

interface BarraDeCenariosProps {
  nome: string;
  onNome: (v: string) => void;
  /** Próximo nome da série, mostrado como exemplo no campo. */
  sugestao: string;
  selecionados: number;
  isSaving: boolean;
  isExporting: boolean;
  /** Com um cenário aberto para leitura, salvar fica desligado: salvaria o plano atual, não o que está em tela. */
  vendoCenario: boolean;
  onSalvar: () => void;
  onComparar: () => void;
  onExportar: () => void;
}

/** Salvar o plano atual como cenário, comparar e exportar. */
export const BarraDeCenarios: FC<BarraDeCenariosProps> = ({
  nome,
  onNome,
  sugestao,
  selecionados,
  isSaving,
  isExporting,
  vendoCenario,
  onSalvar,
  onComparar,
  onExportar,
}) => (
  <div className="mb-4">
    <div className="flex flex-wrap items-end gap-2">
      <label className="text-xs text-content-muted">
        Salvar o plano atual como cenário
        <input
          aria-label="Nome do cenário"
          value={nome}
          placeholder={sugestao ? `ex.: ${sugestao}` : 'Nome do cenário'}
          onChange={(e) => onNome(e.target.value)}
          className="mt-1 block w-64 rounded-lg border border-theme-border bg-surface-secondary px-3 py-1.5 text-sm"
        />
      </label>
      <button
        type="button"
        data-testid="seop-salvar-cenario"
        onClick={onSalvar}
        disabled={isSaving || vendoCenario}
        title={vendoCenario ? 'Volte ao plano atual para salvar um cenário novo' : undefined}
        className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
      >
        Salvar cenário
      </button>
      <button
        type="button"
        data-testid="seop-comparar"
        onClick={onComparar}
        disabled={isSaving || selecionados < 2}
        className={BOTAO_SECUNDARIO}
      >
        Comparar selecionados
      </button>
      <button
        type="button"
        data-testid="seop-exportar-report"
        onClick={onExportar}
        disabled={isSaving || isExporting || selecionados === 0}
        title={selecionados === 0 ? 'Escolha ao menos um cenário para exportar' : 'Gera o Report de Cenários em .xlsx'}
        className={BOTAO_SECUNDARIO}
      >
        {isExporting ? 'Gerando…' : 'Exportar report (.xlsx)'}
      </button>
    </div>
    <p className="mt-2 text-xs text-content-muted">
      Salvar guarda uma foto do plano atual com esse nome. O plano atual continua igual; a foto fica na lista para abrir,
      comparar ou tornar o plano atual de novo depois.
    </p>
  </div>
);
